import React from 'react';
import { Info, Shield } from 'lucide-react';

/**
 * MarketplaceDisclaimer — measured, organized liability disclaimer.
 *
 * Copy updated Feb 2026 to reflect the B2B pivot: CaraLoom is a
 * care-coordination software platform used by hospitals and receiving
 * post-acute providers. It is NOT a consumer marketplace, staffing
 * agency, or employer of clinicians. We do not broker family-to-
 * clinician bookings on this site. Component name is preserved to
 * respect RULE ZERO (no rewrites of existing architecture) and the
 * frontend allowlist wiring; only the copy has changed.
 *
 * Variants: banner | inline | micro | full (organized footer block)
 */

const FULL_SECTIONS = [
  {
    title: 'A software platform, not a care provider',
    body: (
      <>CaraLoom is a care-coordination software platform used by hospitals and receiving post-acute providers — not a healthcare provider, staffing agency, or employer of clinicians. Clinical decisions, staffing, and treatment remain the responsibility of the licensed organizations and clinicians who use the platform.</>
    ),
  },
  {
    title: 'Coverage checks & orders',
    body: (
      <>Eligibility and authorization checks surface information from third-party sources to help planners and providers coordinate discharge. They do not create coverage, guarantee payment, or replace payer determinations. Orders and consents signed in the platform carry a full audit trail; during pilot, e-signature runs in a non-binding test mode until vendor agreements are complete.</>
    ),
  },
  {
    title: 'Profiles & verification',
    body: (
      <>Provider organization profiles and clinician credentials are furnished by the organizations themselves and verified at onboarding, with license status monitored on an ongoing basis. No verification is ever exhaustive — hospitals remain responsible for their own due diligence when selecting receiving providers. See what we check on our <a href="/trust" className="text-cn-primary dark:text-cn-sage-300 font-semibold underline">Trust</a> page.</>
    ),
  },
  {
    title: 'AI guidance',
    body: (
      <>AI features on the platform are informational tools that assist licensed users; they are not a substitute for professional medical advice, diagnosis, or treatment, and every consequential action is reviewed by a human user before it takes effect.</>
    ),
  },
  {
    title: 'Emergencies',
    body: <>If you are experiencing a medical emergency, call 911.</>,
  },
  {
    title: 'Promotions & marks',
    body: (
      <>Pilot pricing and program terms are subject to the written agreement between CaraLoom and the participating organization. CaraLoom℠ and &ldquo;Care, woven into one continuous thread.&rdquo; are service marks of CaraLoom, Inc. © 2026 CaraLoom, Inc. Your use of this site is subject to our <a href="/terms" className="text-cn-primary dark:text-cn-sage-300 font-semibold underline">Terms of Service</a> and <a href="/privacy" className="text-cn-primary dark:text-cn-sage-300 font-semibold underline">Privacy Policy</a>.</>
    ),
  },
];

export const MarketplaceDisclaimer = ({ variant = 'inline', className = '' }) => {
  if (variant === 'full') {
    return (
      <div className={`text-xs text-cn-text-muted leading-relaxed ${className}`} data-testid="marketplace-disclaimer-full">
        <div className="grid gap-4 sm:grid-cols-2">
          {FULL_SECTIONS.map((s) => (
            <div key={s.title} data-testid={`disclaimer-section-${s.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text mb-1">{s.title}</p>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className={`flex items-start gap-3 rounded-2xl border border-cn-border bg-cn-bone-100 dark:bg-cn-ink-800 px-5 py-4 ${className}`} data-testid="marketplace-disclaimer-banner">
        <Shield className="w-5 h-5 text-cn-secondary flex-shrink-0 mt-0.5" strokeWidth={2} />
        <p className="text-sm text-cn-text-muted leading-relaxed">
          <span className="font-semibold text-cn-text">CaraLoom is a care-coordination software platform, not a direct care provider.</span>{' '}
          Hospitals and receiving providers use CaraLoom to coordinate discharge; clinical decisions and staffing remain with the licensed organizations and clinicians who use the platform. See our <a href="/trust" className="text-cn-primary dark:text-cn-sage-300 font-semibold underline">Trust</a> page for what we verify.
        </p>
      </div>
    );
  }

  if (variant === 'micro') {
    return (
      <p className={`text-[11px] text-cn-text-muted/80 ${className}`} data-testid="marketplace-disclaimer-micro">
        CaraLoom is care-coordination software used by hospitals and receiving providers. We don&apos;t provide care directly or employ clinicians. <a href="/terms" className="underline">Terms</a> · <a href="/privacy" className="underline">Privacy</a> · <a href="/trust" className="underline">Trust</a>.
      </p>
    );
  }

  // inline (default)
  return (
    <p className={`text-xs text-cn-text-muted leading-relaxed ${className}`} data-testid="marketplace-disclaimer-inline">
      <Info className="w-3 h-3 inline mr-1 -mt-0.5" />
      CaraLoom is a care-coordination software platform used by hospitals and receiving providers — not a healthcare provider, staffing agency, or employer of clinicians. Use of this site is subject to our <a href="/terms" className="underline">Terms</a>.
    </p>
  );
};

export default MarketplaceDisclaimer;
